/**
 * Montants en Ariary.
 *
 * L'Ariary n'a pas de subdivision en usage : les montants sont des entiers, et
 * il n'existe pas de colonne devise dans le modèle (décision vision.md).
 * Tout ce fichier tient pour acquis qu'on ne manipule QUE des entiers — pas de
 * division, pas de flottant, donc aucune erreur d'arrondi possible sur un solde.
 */

const GROUPER = new Intl.NumberFormat('fr-MG', { maximumFractionDigits: 0 })

/** Espace insécable ordinaire (U+00A0). */
const NBSP = '\u00a0'

/** Vrai signe moins (U+2212), et non un trait d'union. */
const MINUS = '\u2212'

/**
 * Intl produit en français une espace FINE insécable (U+202F) comme séparateur
 * de milliers. Typographiquement juste, mais toutes les polices ne la portent
 * pas — un rendu de secours affiche alors un rectangle au milieu d'un montant,
 * sur une app dont les montants sont l'information principale. On retombe donc
 * sur l'insécable ordinaire, universellement disponible et tout aussi efficace
 * contre un retour à la ligne au milieu d'un nombre.
 */
function group(value: number): string {
  return GROUPER.format(Math.trunc(value))
    .replace(/\u202f/g, NBSP)
    // Intl signe les négatifs avec un trait d'union ASCII, plus court et plus
    // bas que le vrai signe moins. Sans cette normalisation, le solde et les
    // lignes d'opération n'affichent pas le même glyphe pour la même idée.
    .replace(/^-/, MINUS)
}

/** « 25 000 Ar » */
export function formatAriary(amount: number): string {
  return group(amount) + NBSP + 'Ar'
}

/** « 25 000 » — sans unité, pour les cas où « Ar » est déjà affiché à côté. */
export function formatAmount(amount: number): string {
  return group(amount)
}

/** « + 25 000 Ar » / « − 25 000 Ar ». Le signe vient du sens, jamais du montant. */
export function formatSigned(amount: number, direction: 'income' | 'expense'): string {
  // Zéro n'a pas de sens : « −0 Ar » se lit comme une erreur d'affichage, et
  // « +0 Ar » comme une rentrée d'argent qui n'a pas eu lieu.
  if (amount === 0) return formatAriary(0)
  // Signe collé au nombre, comme le fait le solde. Une espace ici donnait
  // « − 25 000 Ar » sur les lignes et « −25 000 Ar » sur le solde : deux
  // écritures du même montant sur le même écran.
  const sign = direction === 'income' ? '+' : MINUS
  return sign + formatAriary(Math.abs(amount))
}

/**
 * Lit ce que l'utilisateur a tapé dans un champ de montant.
 *
 * Tolère les espaces (y compris insécables), les points et les virgules de
 * séparation de milliers — les habitudes de saisie varient et refuser
 * « 25 000 » serait absurde. Renvoie null si rien d'exploitable : au champ de
 * décider quoi en dire.
 */
export function parseAmount(input: string): number | null {
  const digits = input.replace(/[\s.,\u00a0\u202f]/g, '')
  if (!/^\d+$/.test(digits)) return null
  const value = Number(digits)
  return Number.isSafeInteger(value) ? value : null
}

/**
 * Le plafond d'un montant, en Ariary \u2014 d\u00e9cision 2026-09-05 (Faly).
 *
 * Douze chiffres. D\u00e9fini ICI plut\u00f4t que dans `limites.ts` pour \u00e9viter que les
 * deux fichiers s'importent l'un l'autre ; `limites.ts` le r\u00e9-exporte sous le
 * nom qu'utilisent les \u00e9crans.
 */
export const MAX_AMOUNT = 999_999_999_999

/**
 * Le montant, seulement s'il tient dans les bornes.
 *
 * \u00c0 pr\u00e9f\u00e9rer \u00e0 `parseAmount` partout o\u00f9 la valeur part en base : le serveur
 * refuse au-del\u00e0 du plafond (`Bounds::MAX_AMOUNT`), et une ligne refus\u00e9e reste
 * coinc\u00e9e dans la file de synchronisation sans que personne le voie.
 */
export function parseAmountBounded(input: string): number | null {
  const value = parseAmount(input)
  return value !== null && value <= MAX_AMOUNT ? value : null
}
